import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { loginAction, forgotPasswordAction } from '../../actions/authActions';
import {
  InputEmail, InputPassword, Button, ErrorField,
} from '../../lib/HooksFormFields';
import styles from './Auth.module.scss';
import { TOOL_ID, API_URL } from '../../actions';

export default function Login() {
  const TOOL_KEY = 'octadmin';
  const dispatch = useDispatch();
  const history = useHistory();
  const { userAuth, error, message } = useSelector((state) => state.auth);
  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: {
      errors,
    },
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);

  const clearError = useCallback(() => {
    dispatch({
      type: 'ERROR_AUTH',
      payload: null,
    });
  }, [dispatch]);

  const startSsoLogin = useCallback(() => {
    // Démarre le flow SAML côté API, qui redirigera ensuite vers /sso/<token>.
    // Important: on passe le nom d'outil (clé de config côté API).
    const toolParam = encodeURIComponent(TOOL_KEY);
    window.location.href = `${API_URL}/auth/login-saml?tool=${toolParam}`;
  }, [TOOL_KEY]);

  function onSubmit(values) {
    setIsLoading(true);
    clearError();
    loginAction(dispatch, values);
  }

  function submitForgot() {
    clearError();
    if (!getValues('email')) {
      return setError('email', {
        type: 'manual',
        message: "L'email est requis",
      });
    }
    return forgotPasswordAction(dispatch, { email: getValues('email') });
  }

  useEffect(() => {
    clearError();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const passwordMode = params.get('password') === '1';
    const ssoError = params.get('ssoError');
    const attempted = sessionStorage.getItem('octadmin_sso_attempted') === '1';

    if (passwordMode || ssoError) {
      setUsePasswordLogin(true);
      setIsLoading(false);
      return;
    }

    // Tentative SSO en premier (une fois par session),
    // avec fallback possible vers le login mot de passe.
    if (!attempted) {
      sessionStorage.setItem('octadmin_sso_attempted', '1');
      setIsLoading(true);
      startSsoLogin();
    }
  }, [startSsoLogin]);

  useEffect(() => {
    setIsLoading(false);
  }, [error, message]);

  useEffect(() => {
    if (userAuth?.tools?.find((p) => p.tool === TOOL_ID && p.role === 'Admin')) {
      history.push('/dashboard');
    }
  }, [userAuth?.tools]);

  return (
    <div className={styles.container}>
      <div className={styles['col-img']}></div>
      <div className={styles['col-form']}>
        <form>
          <p className={`${styles.title} bold`}>
            <span className={`${styles.name} bold`} >OCTADMIN</span><br/>
            <span className="regular">By </span><span>Octapharma</span>
          </p>
          <InputEmail
            name='email'
            label='Identifiant'
            control={control}
            className={'login'}
            required
            placeholder={'Email'}
          />
          <ErrorField message={errors.email?.message}/>
          {usePasswordLogin && (
            <>
              <InputPassword
                name='password'
                label='Mot de passe'
                control={control}
                className={'login'}
                rules={{ required: 'le mot de passe est requis' }}
                password
                placeholder={'Mot de passe'}
                />
              <ErrorField message={errors.password?.message}/>
            </>
          )}
          <div className={styles['btn-container']}>
            <Button
              handleClick={usePasswordLogin ? handleSubmit(onSubmit) : startSsoLogin}
              isLoading={isLoading}>
              {usePasswordLogin ? 'CONNEXION' : 'CONNEXION SSO'}
            </Button>
            {!usePasswordLogin && (
              <button
                type="button"
                onClick={() => {
                  setUsePasswordLogin(true);
                  const url = new URL(window.location.href);
                  url.searchParams.set('password', '1');
                  window.history.replaceState({}, '', url.toString());
                }}
                className={styles.forgot}>
                Utiliser un mot de passe
              </button>
            )}
            <button
              type="button"
              onClick={() => submitForgot()}
              className={styles.forgot}>
              Mot de passe oublié ?
            </button>
          </div>
          <p className={styles.message}>
            {error === 401
              && <span className={styles.error}>
                L&apos;email ou le mot de passe<br/>est incorrect.
              </span>
            }
            {error?.data?.error === 'Your request could not be processed as entered. Please try again.'
              && <span className={styles.error}>
                Votre email est incorrect
              </span>
            }
            {message === 'Please check your email for the link to reset your password.'
              && <span>
                Veuillez vérifier votre courrier électronique<br/>
                pour réinitialiser votre mot de passe.
              </span>
            }
            {new URLSearchParams(window.location.search).get('ssoError') === 'invalid_token'
              && <span className={styles.error}>
                Erreur SSO, veuillez utiliser le login mot de passe.
              </span>
            }
          </p>
        </form>
        <div className={styles.links}>
          <Link to="/data-policy" className={styles.link}>Politique de confidentialités</Link>
          <Link to="/transparency" className={styles.link}>Transparence des liens d’intérêts</Link>
        </div>
      </div>
    </div>
  );
}
